import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { IconType } from '@flywheel-io/vision';
import { catchError, EMPTY, expand, Observable, retry, shareReplay, switchMap, throwError, timer } from 'rxjs';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { REFRESH_INTERVAL_SMALL } from '../project-imports.constants';
import { ImportDetailsDialogAction } from '../project-imports.enum';
import { ImportDetailsDialogData, ImportDetailsDialogResult } from '../project-imports.interface';
import { FwProgressBarColor, FwProgressBarStatusColor } from '../../shared/enums/fw-progress-bar.type';
import { XferImport, XferImportProgress, XferOperationStatus } from '../../shared/models/xfer.model';
import { FwAlertColor } from '../../shared/types/fw-alert-color.type';
import { FwButtonColor } from '../../shared/types/fw-button-color.type';
import { OperationDetailsTableItem } from '../../xfer/components/operations-details-table/operations-details-table.interface';

@Component({
  host: { 'test-id': 'import-details-dialog' },
  selector: 'import-details-dialog',
  styleUrls: ['./import-details-dialog.component.scss'],
  templateUrl: './import-details-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportDetailsDialog implements OnInit {
  protected createdBy: string = this.data.createdBy;
  protected progress$: Observable<XferImportProgress>;
  protected xferImport: XferImport = this.data.import;
  protected toastColor: FwAlertColor;
  protected retryImportColor: FwButtonColor;
  protected isFinished = false;
  protected isCancelling = false;
  protected isStarting = false;
  protected alertTitle = '';
  protected alertDescription = '';
  protected alertDescriptionCount = '';
  protected alertIcon: IconType;
  protected alertSeverity: FwAlertColor;
  protected progressBarColor = FwProgressBarColor.Primary;
  protected fileStatisticsTableData: OperationDetailsTableItem[] = [];
  protected impactedContainers: OperationDetailsTableItem[] = [];
  protected malwareDetection: OperationDetailsTableItem[] = [];
  protected progressBarStatusText: string;
  protected progressBarStatusColor: FwProgressBarStatusColor;

  protected readonly status = XferOperationStatus;

  private readonly thresholdException = 'Reached failure threshold';

  constructor(
    @Inject(DIALOG_DATA) private data: ImportDetailsDialogData,
    public dialogRef: DialogRef<ImportDetailsDialogResult, ImportDetailsDialog>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
  ) {
  }

  ngOnInit(): void {
    this.initProgress();
  }

  get statusReason(): string {
    const message = this.data.import.status_reason;

    if (!message || message?.startsWith(this.thresholdException)) {
      return '';
    }

    return message[0].toUpperCase() + this.data.import.status_reason.slice(1) + '.';
  }

  protected downloadReport(progress: XferImportProgress): void {
    this.dialogRef.close({ action: ImportDetailsDialogAction.DownloadImportReport, progress });
  }

  protected downloadConflictReport(progress: XferImportProgress): void {
    this.dialogRef.close({ action: ImportDetailsDialogAction.DownloadConflictReport, progress });
  }

  protected rerunImport(): void {
    this.dialogRef.close({ action: ImportDetailsDialogAction.Rerun });
  }

  protected cancelImport(): void {
    this.dialogRef.close({ action: ImportDetailsDialogAction.Cancel });
  }

  protected isTerminal(status: XferOperationStatus): boolean {
    return status === XferOperationStatus.Cancelled || status === XferOperationStatus.Completed || status === XferOperationStatus.Failed;
  }

  private updateProgressBarColor(progress: XferImportProgress): void {
    if (progress.status2 === XferOperationStatus.Cancelled || progress.status2 === XferOperationStatus.Canceled || progress.status2 === XferOperationStatus.Cancelling || progress.status2 === XferOperationStatus.Canceling) {
      this.progressBarColor = FwProgressBarColor.Primary;
    } else if (progress.totals.failed_files || progress.totals.malware_files || progress.status2 === XferOperationStatus.Failed) {
      this.progressBarColor = FwProgressBarColor.Red;
    } else if (progress.totals.skipped_files) {
      this.progressBarColor = FwProgressBarColor.Orange;
    } else if (progress.status2 === XferOperationStatus.Completed && !progress.status_reason) {
      this.progressBarColor = FwProgressBarColor.Green;
      return;
    } else {
      this.progressBarColor = FwProgressBarColor.Primary;
    }
  }

  private initProgress(): void {
    const loop$ = this.coreApi.xfer.imports.progress(this.xferImport._id).pipe(
      retry({ delay: REFRESH_INTERVAL_SMALL * 1000 })
    );
    this.progress$ = loop$.pipe(
      expand((progress) => {
        this.updateData(progress);

        if (this.isTerminal(progress.status2)) {
          return EMPTY;
        }
        return timer(REFRESH_INTERVAL_SMALL * 1000).pipe(switchMap(() => loop$));
      }),
      catchError(error => {
        this.logger.error(error);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private updateData(progress: XferImportProgress): void {
    this.isCancelling = progress.status2 === this.status.Cancelling || progress.status2 === this.status.Canceling;
    this.isFinished = progress.status2 === this.status.Canceled || progress.status2 === this.status.Cancelled || progress.status2 === this.status.Failed || progress.status2 === this.status.Completed;

    this.updateToastStatus(progress);
    this.updateRetryImportColor(progress);
    this.updateTablesData(progress);
    this.updateIsCancelable(progress.status2);
    this.updateProgressBarColor(progress);
    this.updateAlertData(progress);
    this.updateProgressBarStatusText(progress);
    this.updateProgressBarStatusColor(progress);
  }

  private updateToastStatus(progress: XferImportProgress): void {
    if ((progress.status2 === XferOperationStatus.Completed && progress.totals.failed_files) || progress.status2 === XferOperationStatus.Cancelled) {
      this.toastColor = 'warning';
      return;
    }

    if (progress.status2 === XferOperationStatus.Completed) {
      this.toastColor = 'success';
      return;
    }

    if (progress.status2 === XferOperationStatus.Failed) {
      this.toastColor = 'error';
      return;
    }

    this.toastColor = undefined;
  }

  private updateIsCancelable(status: XferOperationStatus): void {
    this.isStarting = status === this.status.Pending || status === this.status.Running;
  }

  private updateRetryImportColor(progress: XferImportProgress): void {
    if (!this.xferImport.can_rerun) {
      this.retryImportColor = null;
      return;
    }

    if ((progress.status2 === XferOperationStatus.Completed && progress.totals.failed_files) || progress.status2 === XferOperationStatus.Cancelled) {
      this.retryImportColor = 'warning';
      return;
    }

    if (progress.status2 === XferOperationStatus.Completed) {
      this.retryImportColor = 'success';
      return;
    }

    if (progress.status2 === XferOperationStatus.Failed) {
      this.retryImportColor = 'danger';
      return;
    }

    this.retryImportColor = undefined;
  }

  private updateTablesData(progress: XferImportProgress): void {
    this.fileStatisticsTableData = [{
      title: 'importDetailsDialog.labels.imported',
      count: progress.totals.success_files + progress.totals.review_files,
    }, {
      title: 'importDetailsDialog.labels.skipped',
      count: progress.totals.skipped_files,
      isWarning: progress.totals.skipped_files > 0,
    }, {
      title: 'importDetailsDialog.labels.failed',
      count: progress.totals.failed_files,
      isDanger: progress.totals.failed_files > 0,
    }];
    this.impactedContainers = [{
      title: 'importDetailsDialog.labels.subjects',
      count: progress.totals.created_subjects + progress.totals.updated_subjects + progress.totals.skipped_subjects,
    }, {
      title: 'importDetailsDialog.labels.sessions',
      count: progress.totals.created_sessions + progress.totals.updated_sessions + progress.totals.skipped_sessions,
    }, {
      title: 'importDetailsDialog.labels.acquisitions',
      count: progress.totals.created_acquisitions + progress.totals.updated_acquisitions + progress.totals.skipped_acquisitions,
    }];
    this.malwareDetection = [{
      title: 'importDetailsDialog.labels.detections',
      count: progress.totals.malware_files,
      isDanger: progress.totals.malware_files > 0,
    }];
  }

  private updateAlertData(progress: XferImportProgress): void {
    this.alertTitle = this.getAlertTitle(progress);
    this.alertIcon = this.getAlertIcon(progress);
    this.alertSeverity = this.getAlertSeverity(progress);
    this.alertDescription = this.getAlertDescription(progress);
    this.updateAlertDescriptionCount(progress);
  }

  private updateProgressBarStatusColor(progress: XferImportProgress): void {
    this.progressBarStatusColor = null;

    if (progress.status2 === XferOperationStatus.Cancelling || progress.status2 === XferOperationStatus.Canceling || progress.status2 === XferOperationStatus.Cancelled || progress.status2 === XferOperationStatus.Canceled) {
      this.progressBarStatusColor = FwProgressBarStatusColor.Grey;
    } else if (progress.totals.malware_files) {
      this.progressBarStatusColor = FwProgressBarStatusColor.Red;
    }
  }

  private updateProgressBarStatusText(progress: XferImportProgress): void {
    const failedFiles = progress.totals.failed_files;
    const malwareFiles = progress.totals.malware_files;
    const skippedFiles = progress.totals.skipped_files;
    const prefix = 'importDetailsDialog.progress.status.';

    if (progress.status2 === XferOperationStatus.Completed) {
      if (failedFiles && malwareFiles || skippedFiles && malwareFiles || failedFiles && skippedFiles) {
        this.progressBarStatusText = `${prefix}exceptionsFound`;
      }
      else if (malwareFiles) {
        this.progressBarStatusText = malwareFiles === 1 ? `${prefix}malwareDetection` : `${prefix}malwareDetections`;
      }
      return;
    }

    this.progressBarStatusText = '';
  }

  private getAlertDescription(progress: XferImportProgress): string {
    const skippedFiles = progress.totals.skipped_files;
    const failedFiles = progress.totals.failed_files;
    const malwareFiles = progress.totals.malware_files;

    if (this.isFinished) {
      if (progress.status2 === XferOperationStatus.Failed) {
        const reason = progress.status_reason;

        if (reason?.startsWith(this.thresholdException)) {
          return 'importDetailsDialog.status.failed.thresholdException';
        }

        return 'importDetailsDialog.status.finished.withException.description';
      }

      if (progress.status2 === XferOperationStatus.Cancelled || progress.status2 === XferOperationStatus.Canceled) {
        return 'importDetailsDialog.status.cancelled.description';
      }

      if (skippedFiles || failedFiles || malwareFiles) {
        return 'importDetailsDialog.status.finished.withException.description';
      } else {
        return 'importDetailsDialog.status.finished.completed.description';
      }
    } else {
      return 'importDetailsDialog.status.progress.description';
    }
  }

  private getAlertTitle(progress: XferImportProgress): string {
    const skippedFiles = progress.totals.skipped_files;
    const failedFiles = progress.totals.failed_files;
    const malwareFiles = progress.totals.malware_files;

    if (this.isFinished) {
      if (progress.status2 === XferOperationStatus.Failed) {
        return 'importDetailsDialog.status.failed.title';
      }

      if (progress.status2 === XferOperationStatus.Cancelled || progress.status2 === XferOperationStatus.Canceled) {
        return 'importDetailsDialog.status.cancelled.title';
      }

      if (skippedFiles + failedFiles === progress.totals.discovered_files) {
        return 'importDetailsDialog.status.finished.withException.noFilesImported';
      } else if (failedFiles || malwareFiles) {
        return 'importDetailsDialog.status.finished.withException.withFailedFiles';
      } else if (skippedFiles) {
        return 'importDetailsDialog.status.finished.withException.withSkippedFiles';
      }

      if (progress.status2 === XferOperationStatus.Completed) {
        return 'importDetailsDialog.status.finished.completed.title';
      }
    } else {
      if (skippedFiles && failedFiles && malwareFiles) {
        return 'importDetailsDialog.status.progress.withSkippedFailedAndMalwareDetections';
      } else if (skippedFiles && failedFiles) {
        return 'importDetailsDialog.status.progress.withSkippedAndFailedFiles';
      } else if (skippedFiles && malwareFiles) {
        return 'importDetailsDialog.status.progress.withSkippedAndMalwareDetections';
      } else if (failedFiles && malwareFiles) {
        return 'importDetailsDialog.status.progress.withFailedAndMalwareDetections';
      } else if (skippedFiles) {
        return 'importDetailsDialog.status.progress.withSkippedFiles';
      } else if (failedFiles) {
        return 'importDetailsDialog.status.progress.withFailedFiles';
      } else if (malwareFiles) {
        return 'importDetailsDialog.status.progress.withMalwareDetections';
      } else {
        return 'importDetailsDialog.status.progress.title';
      }
    }

    return '';
  }

  private getAlertIcon(progress: XferImportProgress): IconType {
    const skippedFiles = progress.totals.skipped_files;
    const failedFiles = progress.totals.failed_files;
    const malwareFiles = progress.totals.malware_files;

    if (failedFiles || malwareFiles || progress.status2 === XferOperationStatus.Cancelled || progress.status2 === XferOperationStatus.Canceled || progress.status2 === XferOperationStatus.Failed) {
      return 'warning-circle';
    }
    else if (skippedFiles) {
      return 'warning';
    }
    else if (progress.status2 === XferOperationStatus.Completed) {
      return 'done-check-tracked';
    } else {
      return 'warning-circle';
    }
  }

  private getAlertSeverity(progress: XferImportProgress): FwAlertColor {
    const skippedFiles = progress.totals.skipped_files;
    const failedFiles = progress.totals.failed_files;
    const malwareFiles = progress.totals.malware_files;

    if (progress.status2 === XferOperationStatus.Cancelled || progress.status2 === XferOperationStatus.Canceled) {
      return 'info';
    }
    else if (failedFiles || malwareFiles || progress.status2 === XferOperationStatus.Failed) {
      return 'error';
    }
    else if (skippedFiles) {
      return 'warning';
    }
    else if (progress.status2 === XferOperationStatus.Completed) {
      return 'success';
    }
    else {
      return 'info';
    }
  }

  private updateAlertDescriptionCount(progress: XferImportProgress): void {
    const reason = progress.status_reason;
    if (progress.status2 === XferOperationStatus.Failed && reason?.startsWith(this.thresholdException)) {
      this.alertDescriptionCount = this.getThresholdValue(reason);
    }
  }

  private getThresholdValue(value: string): string {
    const pattern = new RegExp(`^${this.thresholdException} (\\d+)%$`);
    const match = value.match(pattern);
    return parseInt(match[1], 10) + '%';
  }
}
